// Bundle model for Post a Project (Atrium Stage 7) — the pre-built packages
// (Silver / Gold / Platinum / Ultimate Fiction / Audiobook Narration).
//
// Source of truth = the Rails backend (NOT the design prototype):
//   make_bundle!              app/models/job_postings/job_posting.rb:504  (which children spawn)
//   calculate_bundle_cost     job_posting.rb:2310  (charge math)
//   base_bundle_cost          job_posting.rb:2328  (DB-driven, AI branch, − BUNDLE_DISCOUNTS)
//   BUNDLE_TIER_COST / *_BUNDLE / BUNDLE_DISCOUNTS  constants.rb
//   PostingCalculator bundle_params  app/services/internal/posting_calculator/calculate.rb
//
// A bundle is a curated queue of the existing book_writing + cover_design configs
// (driven with ctx.bundle on) plus auto-derived children (outline / narration /
// formatting / description). This module holds the composition, the price payload
// the /post/price endpoint understands, and the rail estimate that mirrors
// calculate_bundle_cost. The server /post/price total is authoritative; these
// lines are the first-paint estimate.

import { RATES, ALL_TIER_VALUE } from "./options"

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100

// Exact backend bundle_tier strings (constants.rb). A short key 422s.
export const SILVER = "Silver"
export const GOLD = "Gold"
export const PLATINUM = "Platinum"
export const FICTION = "Ultimate Fiction"
export const NARRATION = "Audiobook Narration"

// What each bundle includes, mirroring make_bundle! + create_<tier>_bundle:
//   outline       null | "Professional" | "Plot"  (the included outline kind)
//   outlineChild  true when an outline_posting CHILD exists (Platinum/Narration) —
//                 these validate outline__three (collaborative). Fiction's plot
//                 outline is created at ORDER time (no child, nothing to collect).
//   description   a book_description / fiction-blurb child is included
//   narration     an audiobook narration child is included (Narration bundle)
//   forceFiction  the manuscript is locked to fiction (Ultimate Fiction)
export const BUNDLE_COMPOSITION = {
  [SILVER]: { outline: null, outlineChild: false, description: false, narration: false, forceFiction: false },
  [GOLD]: { outline: null, outlineChild: false, description: true, narration: false, forceFiction: false },
  [PLATINUM]: { outline: "Professional", outlineChild: true, description: true, narration: false, forceFiction: false },
  [FICTION]: { outline: "Plot", outlineChild: false, description: true, narration: false, forceFiction: true },
  [NARRATION]: { outline: "Professional", outlineChild: true, description: true, narration: true, forceFiction: false },
}

export function bundleComposition(tier) {
  return BUNDLE_COMPOSITION[tier] || BUNDLE_COMPOSITION[GOLD]
}

// The configurable children driven through the wizard engine (reused configs).
// outline (collaborative) + narration (character_voice_count) + the optional
// requirement uploads are collected in the dedicated Deliverables stage instead.
export function bundleQueue() {
  return ["book_writing", "cover_design"]
}

// The outline package_type the bundle stamps on its outline child (submit /
// pricing). Plot for Fiction, Professional for Platinum/Narration, null otherwise.
export function bundleOutlinePackage(tier) {
  const c = bundleComposition(tier)
  if (c.outline === "Plot") return "Plot Outline"
  if (c.outline === "Professional") return "Professional Outline"
  return null
}

// Full (pre-discount) value of the included outline, for the value cards. The
// outline legs reuse the Phase-1 server-driven outlinePrices ({professional,
// plot}, Insider-aware) exactly like the BYO book_writing estimate does
// (bookWriting.js:425), falling back to the RATES seed only when no live
// PriceList is threaded (ctx absent — e.g. the deliverables value cards).
export function bundleOutlineValue(tier, ctx = {}) {
  const c = bundleComposition(tier)
  if (c.outline === "Plot") return ctx.outlinePrices?.plot ?? RATES.PLOT_OUTLINE_TOTAL
  if (c.outline === "Professional") return ctx.outlinePrices?.professional ?? RATES.OUTLINE_COST
  return 0
}

// Silver→Gold/Platinum cover upgrade delta (BUNDLE_COVER_TYPE_COST). Reads the
// server cover-package rates (Insider-aware) when threaded, RATES seed otherwise.
export function bundleCoverDelta(pkg, ctx = {}) {
  // Per-key reads: a null server leaf for any package falls back to the RATES
  // seed for THAT package, never to a null-arithmetic 0/NaN (tuw-app:P-001).
  const silver = ctx.rates?.cover_design_cost?.Silver ?? RATES.COVER_DESIGN_COST.Silver
  if (pkg === GOLD) {
    return round2((ctx.rates?.cover_design_cost?.Gold ?? RATES.COVER_DESIGN_COST.Gold) - silver)
  }
  if (pkg === PLATINUM) {
    return round2((ctx.rates?.cover_design_cost?.Platinum ?? RATES.COVER_DESIGN_COST.Platinum) - silver)
  }
  return 0
}

// ── /post/price payload ───────────────────────────────────────────────────────

// The single bundle entry the calculator's bundle_params understands. The page
// spreads addonPriceFields() on top (job-level managed/QA/QC). Word count floors
// at the bundle minimum so the estimate never reads below the real charge.
export function bundlePricePayload(state = {}) {
  if (!state.bundleTier) return null

  const bw = (state.postings && state.postings.book_writing) || {}
  const cover = (state.postings && state.postings.cover_design) || {}
  const wc = Number(bw.word_count) || 0

  return {
    postingType: "bundle",
    bundleTier: state.bundleTier,
    wordCount: wc > 0 ? wc : RATES.BUNDLE_MIN_WORD_COUNT,
    tier: bw.tier ?? null,
    editingStyle: bw.editing_style ?? null,
    useAi: !!bw.use_ai,
    coverPackageType: cover.package_type || SILVER,
    outlinePackageType: bundleOutlinePackage(state.bundleTier),
    addOns: !!cover.add_ons,
  }
}

// ── rail estimate (mirrors calculate_bundle_cost; server reconciles) ──────────

function writerRate(tier, useAi, ctx = {}) {
  // Per-key: a null server leaf for this tier falls back to the RATES seed for
  // THAT tier, never to 0 (tuw-app:P-001). Returns undefined for an unknown tier
  // so the caller's `rate != null` gate still short-circuits.
  const key = tier === ALL_TIER_VALUE ? "Rising" : tier
  return useAi
    ? (ctx.rates?.ai_writer_cost?.[key] ?? RATES.AI_WRITER_COST[key])
    : (ctx.rates?.writer_cost?.[key] ?? RATES.WRITER_COST[key])
}

// Whether the AI branch drops the included outline/description (base_bundle_cost):
// AI bundles collapse to the Silver-cover base for every tier EXCEPT Fiction.
function aiDropsIncluded(tier, useAi) {
  return !!useAi && tier !== FICTION
}

export function bundleEstimateLines(state = {}, ctx = {}) {
  const tier = state.bundleTier
  if (!tier) return { lines: [], isRange: false }

  const c = bundleComposition(tier)
  const bw = (state.postings && state.postings.book_writing) || {}
  const cover = (state.postings && state.postings.cover_design) || {}

  const useAi = !!bw.use_ai
  const fiction = !!bw.fiction
  const wtier = bw.tier
  const allTier = wtier === ALL_TIER_VALUE
  // BUNDLE_MIN_WORD_COUNT is a word-count floor (not a displayed price) and stays a
  // constant — it is Insider-invariant and does not resolve from the PriceList.
  const words = Math.max(RATES.BUNDLE_MIN_WORD_COUNT, Number(bw.word_count) || 0)

  const lines = []
  let isRange = false

  // Writing — only once a tier is chosen (matches the BYO estimate gate).
  const rate = wtier ? writerRate(wtier, useAi, ctx) : null
  if (rate != null) {
    lines.push({
      label: `Book writing (${words.toLocaleString()} words${allTier ? ", all tiers" : wtier ? `, ${wtier}` : ""}${useAi ? ", AI" : ""})`,
      amount: round2((words / 100) * rate),
    })
    if (allTier) isRange = true
  }

  const dropIncluded = aiDropsIncluded(tier, useAi)

  // Included value (charged at full price inside base_bundle_cost, then the bundle
  // saving comes off below). AI non-fiction bundles drop the outline + description.
  if (!dropIncluded) {
    const outlineVal = bundleOutlineValue(tier, ctx)
    if (outlineVal > 0) {
      lines.push({
        label: c.outline === "Plot" ? "Plot Outline" : "Professional Outline",
        amount: round2(outlineVal),
        sub: "included in bundle",
      })
    }
    if (c.description) {
      const descAmount = ctx.rates?.description_cost ?? RATES.DESCRIPTION_COST
      lines.push({ label: fiction ? "Fiction blurb" : "Book description", amount: descAmount, sub: "included" })
    }
  }

  // Cover — Silver base always, then the chosen upgrade delta.
  const coverSilver = ctx.rates?.cover_design_cost?.Silver ?? RATES.COVER_DESIGN_COST.Silver
  lines.push({ label: "Cover design · Silver", amount: coverSilver, sub: "included" })
  const coverDelta = bundleCoverDelta(cover.package_type, ctx)
  if (coverDelta > 0) {
    lines.push({ label: `Cover upgrade · ${cover.package_type}`, amount: coverDelta })
  }

  // Paperback formatting — every bundle, by length (ceil per 10k).
  const blocks = Math.ceil(words / 10000)
  const paperbackRate = ctx.rates?.formatting_types_cost?.paperback_formatting ?? RATES.FORMATTING_TYPES_COST.paperback_formatting
  lines.push({ label: "Paperback formatting", amount: round2(blocks * paperbackRate) })

  // Line & Essential editing upgrade (book_writing editing_cost = floor(wc/100)*rate).
  const editRate = (ctx.rates?.writing_editing_styles_cost?.[bw.editing_style] ?? RATES.WRITING_EDITING_STYLES_COST[bw.editing_style]) || 0
  if (editRate > 0) {
    lines.push({ label: "Line & Essential editing", amount: round2(Math.floor(words / 100) * editRate) })
  }

  // Narration — Narration bundle, by length (NARRATOR_COST, Ruby floors per 100).
  if (c.narration) {
    const narratorRate = ctx.rates?.narrator_cost ?? RATES.NARRATOR_COST
    lines.push({ label: "Audiobook narration", amount: round2(Math.floor(words / 100) * narratorRate) })
  }

  // Bundle saving (− BUNDLE_DISCOUNTS). AI non-fiction bundles get no discount.
  // BUNDLE_DISCOUNTS is a fixed bundle discount % (not a per-customer PriceList
  // rate) — Insider-invariant, so it stays a constant.
  if (!dropIncluded) {
    const discount = RATES.BUNDLE_DISCOUNTS[tier] || 0
    if (discount > 0) lines.push({ label: "Bundle saving", amount: -discount, muted: true })
  }

  return { lines, isRange }
}

// Whether the Deliverables stage is complete. The only REQUIRED bundle-deliverable
// input is the narration voice count for a FICTION Audiobook Narration bundle
// (narration__one). Collaboration defaults on; uploads are optional.
export function bundleDeliverablesValid(state = {}) {
  const c = bundleComposition(state.bundleTier)
  const bw = (state.postings && state.postings.book_writing) || {}
  if (c.narration && bw.fiction) {
    const n = Number((state.bundleExtras || {}).character_voice_count)
    if (!Number.isFinite(n) || n < 1) return false
  }
  return true
}

// Value-card descriptors for the Deliverables stage + the review summary. The
// struck-through `value` amounts are DISPLAYED prices (BundleDeliverables.jsx:38),
// so — like bundleEstimateLines (C2) — each reads the server-resolved, Insider-aware
// rate `ctx.rates?.<family> ?? RATES.<CONST>` (RATES seed only as the no-live-PriceList
// fallback that keeps first paint working when ctx is absent).
export function bundleDeliverables(state = {}, ctx = {}) {
  const tier = state.bundleTier
  const c = bundleComposition(tier)
  const bw = (state.postings && state.postings.book_writing) || {}
  const fiction = !!bw.fiction
  const cover = (state.postings && state.postings.cover_design) || {}
  const coverPkg = cover.package_type || SILVER

  const items = []

  items.push({
    key: "cover",
    title: `Cover design · ${coverPkg}`,
    included: true,
    value: ctx.rates?.cover_design_cost?.Silver ?? RATES.COVER_DESIGN_COST.Silver,
    note:
      coverPkg === SILVER
        ? "One concept, up to 3 revisions, delivered in eBook, audiobook and paperback formats."
        : `${coverPkg} cover, configured in the cover steps.`,
  })

  // Design's included-row order is description → outline (audit F) — description
  // sits ahead of the outline in the hub's "handled by your team" list.
  if (c.description) {
    items.push({
      key: "description",
      title: fiction ? "Fiction blurb" : "Book description",
      included: true,
      value: ctx.rates?.description_cost ?? RATES.DESCRIPTION_COST,
      note: `A KDP-optimized ${fiction ? "blurb" : "description"} written by a copywriter. HTML included.`,
    })
  }

  if (c.outline) {
    items.push({
      key: "outline",
      title: c.outline === "Plot" ? "Plot Outline" : "Professional Outline",
      included: true,
      // bundleOutlineValue is already ctx-aware (reads ctx.outlinePrices, the Phase-1
      // server-driven outline prices) — thread ctx through, don't re-implement.
      value: bundleOutlineValue(tier, ctx),
      note:
        c.outline === "Plot"
          ? "A collaborative plot outline built with your fiction writer and a developmental editor."
          : "A professional outline researched and composed by your writer.",
    })
  }

  items.push({
    key: "formatting",
    title: "Paperback formatting",
    included: true,
    priced: true,
    note: "Your finished manuscript professionally typeset and print-ready — priced by length.",
  })

  if (c.narration) {
    items.push({
      key: "narration",
      title: "Audiobook narration",
      included: true,
      priced: true,
      note: "Professional voice narration with full production, delivered to ACX standards — priced by length.",
    })
  }

  return items
}
